import './MyOrders.scss';

import get from 'lodash/get';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Spinner, Stack } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router';
import { Button } from '@myome/ui';

import OrderCard from '@components/Cards/OrderCard/orderCard';
import TitleCard from '@components/Cards/TitleCard/titleCard';
import DataDisplayCell from '@components/DataDisplayCell/dataDisplayCell';
import { RerequisitionCallout } from '@components/RerequisitionCallout/RerequisitionCallout';
import { Sanitize } from '@components/Sanitize';
import { MYOME_SUPPORT_EMAIL } from '@constants/constants';
import { Requisition } from '@customTypes/requisition';
import useApp from '@hooks/useApp';
import PageContainer from '@layouts/container/PageContainer';
import { fetchUserTestRequests } from '@store/actions';
import { fetchRerequisitionEligibility } from '@store/actions/patientPortal';
import { Context } from '@store/store';
import { recordGoogleAnalyticsEvent, recordGoogleAnalyticsSend } from '@utils/helpers';

export function MyOrders() {
  const [state, dispatch] = useContext(Context);
  const [searchParams] = useSearchParams();
  const [isRerequisitionEligible, setIsRerequisitionEligible] = useState(false);
  const { userFirstName, userLastName } = useApp();

  const userInfo = useMemo(() => state.userInfo, [state.userInfo]);
  const individualId = searchParams.get('individual') || undefined;

  useEffect(() => {
    recordGoogleAnalyticsSend('pageview', '/my-orders', 'Landing on My Orders Page');
  }, []);

  useEffect(() => {
    let isActive = true;

    fetchUserTestRequests()
      .then(requests => {
        if (isActive) {
          dispatch({ type: 'SET_ALL_TEST_REQUESTS', payload: requests || [] });
        }
      })
      .catch(() => undefined);

    return () => {
      isActive = false;
    };
  }, [dispatch]);

  useEffect(() => {
    let isActive = true;

    fetchRerequisitionEligibility(individualId)
      .then(response => {
        if (isActive) {
          setIsRerequisitionEligible(response.is_eligible === true);
        }
      })
      .catch(() => {
        if (isActive) {
          setIsRerequisitionEligible(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [individualId]);

  const createNameAvatar = () => {
    if (userFirstName && userLastName) {
      return userFirstName.charAt(0) + userLastName.charAt(0);
    }
  };

  return (
    <PageContainer>
      {state.isFetchingUserInfo ? (
        <div className='page_loading_spinner_wrapper text-primary'>
          <Spinner animation='border' />
        </div>
      ) : (
        <div className='header_footer_layout_body home-page'>
          <h1 className='home_page_title mb-0'>
            Welcome <Sanitize>{userFirstName}</Sanitize>!
          </h1>
          {isRerequisitionEligible && <RerequisitionCallout individualId={individualId} />}
          <div className='home_body_wrapper'>
            <section className='home_body_top'>
              {/* TODO: remove TitleCard component and redo these sections so that their headers are semantic */}
              <TitleCard title='Your Reports' height='auto'>
                <p className='fs-xs mt-1 mb-0 text-secondary'>Click on the card to view details</p>
                <ol className='list-style-none p-0 mb-0'>
                  {state.testRequests.map((testRequest: Requisition) => (
                    <li key={testRequest.requisition_id} className='mt-4'>
                      <Link
                        to={`/reports/${testRequest.requisition_id}`}
                        onClick={() => {
                          recordGoogleAnalyticsEvent('User Interaction', 'Button Click', 'Order Card Click');
                        }}
                        style={{ textDecoration: 'none', display: 'block' }}
                      >
                        <OrderCard testRequest={testRequest} onClick={() => {}} />
                      </Link>
                    </li>
                  ))}
                </ol>
              </TitleCard>
            </section>
            <section className='home_body_bottom align-self-start'>
              <Stack gap={3} className='home_user_card'>
                <div className='name_avatar'>{createNameAvatar()}</div>
                <Sanitize as='h2' className='h4'>
                  {userFirstName + ' ' + userLastName}
                </Sanitize>
                <Stack as='ul' className='list-style-none mb-0 ps-0 pe-4'>
                  <li>
                    <DataDisplayCell
                      dataLabel='Email'
                      dataValue={get(userInfo, 'contact_info[0].email', '') as string}
                      phiSanitize
                    />
                  </li>
                  <li>
                    <DataDisplayCell
                      dataLabel='Sex'
                      dataValue={get(userInfo, 'demographics.sex', '') as string}
                      phiSanitize
                    />
                  </li>
                  <li>
                    <DataDisplayCell
                      dataLabel='Phone'
                      dataValue={get(userInfo, 'contact_info[0].phone', '') as string}
                      phiSanitize
                    />
                  </li>
                  <li>
                    <DataDisplayCell
                      dataLabel='Birthdate'
                      dataValue={get(userInfo, 'demographics.dob', '') as string}
                      phiSanitize
                    />
                  </li>
                </Stack>
                <Button
                  to='/account'
                  variant='secondary'
                  onClick={() => {
                    recordGoogleAnalyticsEvent('User Interaction', 'Button Click', 'View Account Details Button');
                  }}
                >
                  View Account Details
                </Button>
              </Stack>
              <TitleCard title='Contact Us'>
                <p className='mb-0 mt-2 fs-xs text-secondary'>
                  {}
                  Got questions? We have you covered. Send us an email at{' '}
                  <a href={`mailto:${MYOME_SUPPORT_EMAIL}`}>{MYOME_SUPPORT_EMAIL}</a> and a member of our team will
                  reach out to you.
                </p>
              </TitleCard>
            </section>
          </div>
        </div>
      )}
    </PageContainer>
  );
}
